import html2canvas from 'html2canvas-pro';

/**
 * Enterprise Screenshot Engines for Feedback Widget
 * 
 * 1. Engine 1: Native Tab Capture (Pixel-Perfect 100% WYSIWYG)
 *    - Sử dụng navigator.mediaDevices.getDisplayMedia({ preferCurrentTab: true })
 *    - Lấy trực tiếp frame video từ GPU/Chromium compositor của tab hiện tại
 *    - 100% chính xác từng pixel, không bao giờ lệch font, méo layout hay bẹp dòng.
 * 
 * 2. Engine 2: Clean Canvas Fallback (html2canvas-pro with Computed Style Mirroring)
 *    - Tự động fallback nếu người dùng hủy chia sẻ tab hoặc trình duyệt không hỗ trợ
 *    - Sao chép trực tiếp computed margins (loại bỏ bug CSS vars calc trong space-y-* của Tailwind)
 *    - Bảo vệ tuyệt đối cấu trúc Flexbox/Grid (không ép display: block lên inline/flex children)
 *    - Lọc bỏ triệt để các phần tử thuộc phân hệ Feedback (chống dính UI/backdrop vào ảnh)
 */

export interface CaptureResult {
    success: boolean;
    dataUrl?: string;
    engine: 'native' | 'canvas';
    error?: any;
}

/**
 * Filter element dùng chung cho các động cơ chụp ảnh
 * Loại trừ triệt để widget panel, backdrop, kính ngắm máy ảnh và các thẻ có data-feedback-ignore
 */
export function isIgnoredScreenshotElement(element: Node | HTMLElement | Element): boolean {
    if (!element || element.nodeType !== 1) { // Node.ELEMENT_NODE
        return false;
    }
    const el = element as HTMLElement;
    const id = el.id || '';
    const className = typeof el.className === 'string' ? el.className : '';
    return id === 'feedback-widget-panel' || 
           id === 'feedback-widget-backdrop' ||
           id === 'feedback-panel' ||
           className.includes('feedback-panel') || 
           className.includes('camera-viewfinder-root') ||
           (typeof el.hasAttribute === 'function' && el.hasAttribute('data-feedback-ignore'));
}

/**
 * Động cơ 1: Chụp màn hình bằng Native Tab Media Stream
 * Yêu cầu: Gọi trong direct User Gesture (sự kiện click trực tiếp của người dùng)
 */
export async function captureWithNativeTab(): Promise<string> {
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('BROWSER_UNSUPPORTED');
    }

    // Các options tối ưu nhất cho Chrome/Edge/Brave/Safari
    const displayMediaOptions: any = {
        video: {
            displaySurface: 'browser', // Ưu tiên chọn tab browser
        },
        audio: false,
        preferCurrentTab: true, // Tự động đề xuất tab hiện tại
        selfBrowserSurface: 'include', // Cho phép tab tự capture chính nó
        systemAudio: 'exclude',
        surfaceSwitching: 'exclude'
    };

    let stream: MediaStream | null = null;
    try {
        stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
    } catch (err: any) {
        // User bấm Cancel dialog hoặc không cấp quyền
        throw err;
    }

    if (!stream) {
        throw new Error('STREAM_EMPTY');
    }

    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = true;
    video.srcObject = stream;

    try {
        await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('VIDEO_READY_TIMEOUT')), 3500);
            video.onloadedmetadata = async () => {
                clearTimeout(timeout);
                try {
                    await video.play();
                    resolve();
                } catch (playErr) {
                    reject(playErr);
                }
            };
            video.onerror = (e) => {
                clearTimeout(timeout);
                reject(e);
            };
        });

        // Đợi 60ms để frame ổn định trên canvas
        await new Promise(r => setTimeout(r, 60));

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || window.innerWidth;
        canvas.height = video.videoHeight || window.innerHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('CANVAS_CTX_ERROR');

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');
        return dataUrl;
    } finally {
        // Luôn giải phóng toàn bộ media tracks ngay lập tức
        if (stream) {
            stream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (_) {}
            });
        }
        video.srcObject = null;
        video.remove();
    }
}

/**
 * Động cơ 2: Chụp bằng html2canvas-pro với bộ Computed Style Mirroring
 * Dùng làm động cơ chụp nhanh tức thì không popup và fallback khi Native Tab bị hủy
 */
export async function captureWithHtml2CanvasClean(): Promise<string> {
    if (document.fonts) {
        try {
            await document.fonts.ready;
        } catch (_) {}
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: dpr,
        logging: false,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        width: window.innerWidth,
        height: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        ignoreElements: isIgnoredScreenshotElement,
        onclone: (clonedDoc: Document) => {
            // 1. Sao chép toàn bộ Stylesheets từ trang gốc sang clonedDoc
            document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
                try {
                    clonedDoc.head.appendChild(link.cloneNode(true));
                } catch (_) {}
            });

            // 2. CSS Base Rules cho bản clone
            const style = clonedDoc.createElement('style');
            style.setAttribute('data-screenshot-patch', 'true');
            style.innerHTML = `
                * {
                    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
                    -webkit-font-smoothing: antialiased !important;
                    text-rendering: optimizeLegibility !important;
                }
                .truncate {
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                    line-height: 1.35em !important;
                }
            `;
            clonedDoc.head.appendChild(style);

            // 3. Computed Style Mirroring (Sao chép trực tiếp khoảng cách margin tính toán)
            // Giải quyết triệt để lỗi Tailwind space-y-* không parse được CSS vars calc()
            try {
                const origElements = document.querySelectorAll('body *');
                const cloneElements = clonedDoc.querySelectorAll('body *');
                const total = Math.min(origElements.length, cloneElements.length);

                for (let i = 0; i < total; i++) {
                    const orig = origElements[i] as HTMLElement;
                    const clone = cloneElements[i] as HTMLElement;
                    if (!orig || !clone || orig.nodeType !== Node.ELEMENT_NODE) continue;

                    const cs = window.getComputedStyle(orig);

                    // Giải cứu Tailwind space-y-* và margin bị coi là 0
                    if (cs.marginTop && cs.marginTop !== '0px') {
                        clone.style.marginTop = cs.marginTop;
                    }
                    if (cs.marginBottom && cs.marginBottom !== '0px') {
                        clone.style.marginBottom = cs.marginBottom;
                    }

                    // Giữ nguyên line-height thực tế
                    if (cs.lineHeight && cs.lineHeight !== 'normal') {
                        clone.style.lineHeight = cs.lineHeight;
                    }
                }
            } catch (err) {
                console.warn('[ScreenshotEngines] Computed style mirroring warning:', err);
            }
        }
    });

    return canvas.toDataURL('image/png');
}

/**
 * Động cơ chụp nhanh tổng hợp
 */
export async function captureWithEnhancedCanvas(): Promise<string> {
    if (typeof window === 'undefined') {
        throw new Error('WINDOW_UNDEFINED');
    }
    return await captureWithHtml2CanvasClean();
}

// Expose helper lên window trong môi trường Dev để phục vụ kiểm thử E2E
if (typeof window !== 'undefined') {
    (window as any).__captureWithEnhancedCanvas = captureWithEnhancedCanvas;
    (window as any).__captureWithHtml2CanvasClean = captureWithHtml2CanvasClean;
}
